import React, { useEffect, useState, useMemo, useRef } from 'react';
import { Container, Table, Spinner, Alert, Dropdown, Badge, Form, Row, Col, Button } from 'react-bootstrap';
import ApiService from '../../services/ApiService';
import { obtenerProductoPorId } from '../../services/productoService';
import './Admin.css';
import '../../components/product/Filtros.css';

function AdminPedidos() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [updatingStatus, setUpdatingStatus] = useState(new Set());
  const [descuentoStockIds, setDescuentoStockIds] = useState(new Set());
  const [productosPrecios, setProductosPrecios] = useState({}); // Cache de precios desde API
  const productosPreciosRef = useRef(productosPrecios);
  productosPreciosRef.current = productosPrecios;
  const [filtroEstado, setFiltroEstado] = useState('');
  const [filtroNombre, setFiltroNombre] = useState('');
  const [filtroTelefono, setFiltroTelefono] = useState('');
  const [costoEnvioEdits, setCostoEnvioEdits] = useState({});
  const [savingCostoEnvioIds, setSavingCostoEnvioIds] = useState(new Set());

  const estadosFiltro = ['CREADO', 'COBRADO', 'ENTREGADO', 'CANCELADO', 'RECHAZADO'];
  const estadosEditables = ['CREADO', 'COBRADO', 'ENTREGADO', 'CANCELADO'];

  const toggleRow = (pedidoId) => {
    setExpandedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(pedidoId)) {
        newSet.delete(pedidoId);
      } else {
        newSet.add(pedidoId);
      }
      return newSet;
    });
  };

  const cambiarEstado = async (pedidoId, nuevoEstado) => {
    if (!pedidoId || typeof pedidoId !== 'string') {
      console.error('[AdminPedidos] ID de pedido inválido:', pedidoId);
      alert('Error: ID de pedido inválido');
      return;
    }
    
    setUpdatingStatus(prev => new Set(prev).add(pedidoId));
    try {
      console.log('[AdminPedidos] Cambiando estado:', { pedidoId, nuevoEstado });
      const url = `/pedidos/${pedidoId}`;
      console.log('[AdminPedidos] URL:', url);
      await ApiService.put(url, { estado: nuevoEstado });
      
      // Actualizar el estado localmente
      setItems(prev => prev.map(item => 
        item._id === pedidoId ? { ...item, estado: nuevoEstado } : item
      ));
    } catch (e) {
      console.error('[AdminPedidos] Error al actualizar estado:', e);
      console.error('[AdminPedidos] Error status:', e?.status);
      console.error('[AdminPedidos] Error payload:', e?.payload);
      alert(e?.payload?.message || e?.message || 'Error al actualizar el estado');
    } finally {
      setUpdatingStatus(prev => {
        const newSet = new Set(prev);
        newSet.delete(pedidoId);
        return newSet;
      });
    }
  };

  const descontarStockManual = async (pedidoId) => {
    if (!pedidoId || typeof pedidoId !== 'string') {
      alert('Error: ID de pedido inválido');
      return;
    }

    setDescuentoStockIds(prev => new Set(prev).add(pedidoId));
    try {
      const response = await ApiService.post(`/pedidos/${pedidoId}/aplicar-stock`);
      const pedidoActualizado = response?.data || response;
      setItems(prev => prev.map(item =>
        item._id === pedidoId ? { ...item, ...pedidoActualizado } : item
      ));
    } catch (e) {
      console.error('[AdminPedidos] Error al descontar stock:', e);
      const details = e?.payload?.details;
      if (Array.isArray(details) && details.length > 0) {
        setItems(prev => prev.map(item =>
          item._id === pedidoId
            ? {
                ...item,
                stockError: true,
                stockProcesado: false,
                stockErrorMessage: e?.payload?.error || e?.payload?.message || e?.message,
                stockErrorDetails: details,
              }
            : item
        ));
      } else {
        alert(e?.payload?.message || e?.message || 'Error al descontar stock');
      }
    } finally {
      setDescuentoStockIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(pedidoId);
        return newSet;
      });
    }
  };

  const costoEnvioActual = (pedido) => {
    const pedidoId = pedido?._id;
    if (pedidoId && Object.prototype.hasOwnProperty.call(costoEnvioEdits, pedidoId)) {
      const n = Number(costoEnvioEdits[pedidoId]);
      if (Number.isFinite(n) && n >= 0) return n;
    }
    return Number(pedido?.costoEnvio) || 0;
  };

  const precioEnvioCobrado = (pedido, subtotalProductos) => {
    if (pedido?.precioEnvio != null && pedido.precioEnvio !== '') {
      const n = Number(pedido.precioEnvio);
      if (Number.isFinite(n) && n >= 0) return n;
    }
    const montoTotal = Number(pedido?.montoTotal);
    if (Number.isFinite(montoTotal) && montoTotal >= subtotalProductos) {
      return montoTotal - subtotalProductos;
    }
    return Number(pedido?.costoEnvio) || 0;
  };

  const formatGananciaPedido = (pedido) => {
    if (pedido?.costoTotal == null) return '—';
    const ganancia = Number(pedido.montoTotal || 0) - Number(pedido.costoTotal) - costoEnvioActual(pedido);
    return `$${ganancia.toLocaleString()}`;
  };

  const guardarCostoEnvio = async (pedido, event) => {
    event?.stopPropagation();
    const pedidoId = pedido?._id;
    if (!pedidoId) return;

    const raw = Object.prototype.hasOwnProperty.call(costoEnvioEdits, pedidoId)
      ? costoEnvioEdits[pedidoId]
      : String(Number(pedido.costoEnvio) || 0);
    const costoEnvio = Number(raw);
    if (!Number.isFinite(costoEnvio) || costoEnvio < 0) {
      alert('El costo de envío debe ser un número mayor o igual a 0');
      return;
    }

    setSavingCostoEnvioIds(prev => new Set(prev).add(pedidoId));
    try {
      const response = await ApiService.put(`/pedidos/${pedidoId}`, { costoEnvio });
      const pedidoActualizado = response?.data || response;
      setItems(prev => prev.map(item =>
        item._id === pedidoId ? { ...item, ...pedidoActualizado } : item
      ));
      setCostoEnvioEdits(prev => {
        const next = { ...prev };
        delete next[pedidoId];
        return next;
      });
    } catch (e) {
      console.error('[AdminPedidos] Error al actualizar costo de envío:', e);
      alert(e?.payload?.message || e?.message || 'Error al actualizar el costo de envío');
    } finally {
      setSavingCostoEnvioIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(pedidoId);
        return newSet;
      });
    }
  };

  const getEstadoBadgeVariant = (estado) => {
    switch (estado) {
      case 'CREADO': return 'warning';
      case 'COBRADO': return 'cobrado';
      case 'ENTREGADO': return 'success';
      case 'CANCELADO': return 'danger';
      case 'RECHAZADO': return 'rechazado';
      default: return 'secondary';
    }
  };

  const getMetodoPagoLabel = (metodoPago) => {
    switch (metodoPago) {
      case 'efectivo': return 'Efectivo';
      case 'mercadopago': return 'Mercado Pago';
      default: return metodoPago || 'Mercado Pago';
    }
  };

  const getModalidadEntregaLabel = (modalidadEntrega) => (
    modalidadEntrega === 'envio' ? 'Envío a todo el país' : 'Retiro por Haedo'
  );

  const getStockBadge = (pedido) => {
    if (pedido.stockProcesado === true) {
      return { label: 'Sí', variant: 'success', title: 'Stock descontado' };
    }
    if (pedido.stockError === true) {
      const details = Array.isArray(pedido.stockErrorDetails) ? pedido.stockErrorDetails : [];
      const detailsText = details
        .map((d) => `${d.nombre || d.productId || 'Producto'}: ${d.error || 'Error'}`)
        .join(' | ');
      return {
        label: 'Error',
        variant: 'danger',
        title: detailsText || pedido.stockErrorMessage || 'Error al descontar stock',
      };
    }
    return { label: 'No', variant: 'secondary', title: 'Stock no descontado' };
  };

  const renderStockBadge = (pedido) => {
    const stockBadge = getStockBadge(pedido);
    return (
      <Badge bg={stockBadge.variant} title={stockBadge.title} className="text-nowrap">
        {stockBadge.label}
      </Badge>
    );
  };

  const calcularTipoCompra = (pedido) => {
    if (!pedido.productos || !Array.isArray(pedido.productos)) return 'minorista';
    
    const cantidadTotal = pedido.productos.reduce((acc, p) => acc + (Number(p.cantidad) || 0), 0);
    const subtotalMayorista = pedido.productos.reduce((acc, p) => {
      const precio = Number(p.precio) || 0;
      const cantidad = Number(p.cantidad) || 0;
      return acc + (precio * cantidad);
    }, 0);
    
    // Regla mayorista: 6 o más productos Y subtotal con precio mayorista >= 60000
    const esMayorista = subtotalMayorista >= 60000 && cantidadTotal >= 6;
    return esMayorista ? 'mayorista' : 'minorista';
  };

  // Función helper para obtener la fecha de un pedido
  const obtenerFechaPedido = (pedido) => {
    if (pedido.fechaCreacion) {
      return new Date(pedido.fechaCreacion).getTime();
    }
    if (pedido.createdAt) {
      if (typeof pedido.createdAt?.toDate === 'function') {
        return pedido.createdAt.toDate().getTime();
      }
      return new Date(pedido.createdAt).getTime();
    }
    if (pedido.fecha) {
      return new Date(pedido.fecha).getTime();
    }
    return 0; // Si no tiene fecha, ponerlo al final
  };

  useEffect(() => {
    const fetchFromApi = async () => {
      try {
        const data = await ApiService.get('/pedidos');
        const list = Array.isArray(data)
          ? data
          : (data?.pedidos || data?.data || data?.items || data?.results || data?.result?.items || data?.data?.items || []);
        console.log('[AdminPedidos] Pedidos API raw:', data);
        console.log('[AdminPedidos] Pedidos API len:', list?.length || 0);
        
        // Ordenar por fecha descendente (más recientes primero)
        const listOrdenado = (list || []).sort((a, b) => {
          const fechaA = obtenerFechaPedido(a);
          const fechaB = obtenerFechaPedido(b);
          return fechaB - fechaA; // Orden descendente
        });
        
        setItems(listOrdenado);
        if (!list || list.length === 0) setError('No hay pedidos para mostrar');
      } catch (e) {
        console.error('[AdminPedidos] Error API:', e);
        if (e?.status === 401 || e?.status === 403) {
          setError('No autorizado. Iniciá sesión para ver los pedidos.');
        } else {
        setError(e?.message || 'No se pudieron cargar los pedidos');
        }
      } finally {
        setLoading(false);
      }
    };
    fetchFromApi();
  }, []);

  // Solo pedidos minoristas sin preciomenor guardado necesitan fetch (mayorista usa precio del pedido).
  useEffect(() => {
    if (expandedRows.size === 0) return;

    const pedidosNecesarios = items.filter((p) => expandedRows.has(p._id));
    const idsToFetch = [];
    const seen = new Set();

    for (const pedido of pedidosNecesarios) {
      const tipoCompra = calcularTipoCompra(pedido);
      if (tipoCompra === 'mayorista') continue;

      for (const producto of pedido.productos || []) {
        const productoId = producto.id;
        if (!productoId || seen.has(productoId)) continue;
        if (producto.preciomenor != null && producto.preciomenor !== undefined) continue;
        if (productosPreciosRef.current[productoId] != null) continue;
        seen.add(productoId);
        idsToFetch.push(productoId);
      }
    }

    if (idsToFetch.length === 0) return;

    let cancelled = false;
    (async () => {
      const entries = await Promise.all(
        idsToFetch.map(async (productoId) => {
          try {
            const producto = await obtenerProductoPorId(productoId);
            return [productoId, Number(producto?.preciomenor) || 0];
          } catch (e) {
            console.error(`[AdminPedidos] Error obteniendo preciomenor para ${productoId}:`, e);
            return [productoId, 0];
          }
        })
      );

      if (cancelled) return;
      setProductosPrecios((prev) => {
        const next = { ...prev };
        for (const [id, price] of entries) {
          next[id] = price;
        }
        return next;
      });
    })();

    return () => {
      cancelled = true;
    };
  }, [expandedRows, items]);

  // Filtrar items según los filtros aplicados
  const itemsFiltrados = useMemo(() => {
    return items.filter(item => {
      // Filtro por estado
      if (filtroEstado && item.estado !== filtroEstado) {
        return false;
      }
      
      // Filtro por nombre
      if (filtroNombre) {
        const nombre = (item.nombreContacto || '').toLowerCase();
        if (!nombre.includes(filtroNombre.toLowerCase())) {
          return false;
        }
      }
      
      // Filtro por teléfono
      if (filtroTelefono) {
        const telefono = (item.telefonoContacto || '').toString();
        if (!telefono.includes(filtroTelefono)) {
          return false;
        }
      }
      
      return true;
    });
  }, [items, filtroEstado, filtroNombre, filtroTelefono]);

  return (
    <Container className="py-4">
      <h5 className="admin-section-title mb-4">Pedidos</h5>
      
      {/* Filtros */}
      <div className="filtros-container mb-4">
        <h5>Filtros</h5>
        <Row className="g-3">
          <Col md={4}>
            <Form.Group className="mb-3">
              <Form.Label>Estado</Form.Label>
              <Form.Select
                value={filtroEstado}
                onChange={(e) => setFiltroEstado(e.target.value)}
              >
                <option value="">Todos los estados</option>
                {estadosFiltro.map(estado => (
                  <option key={estado} value={estado}>{estado}</option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group className="mb-3">
              <Form.Label>Nombre</Form.Label>
              <Form.Control
                type="text"
                placeholder="Buscar por nombre..."
                value={filtroNombre}
                onChange={(e) => setFiltroNombre(e.target.value)}
              />
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group className="mb-3">
              <Form.Label>Teléfono</Form.Label>
              <Form.Control
                type="text"
                placeholder="Buscar por teléfono..."
                value={filtroTelefono}
                onChange={(e) => setFiltroTelefono(e.target.value)}
              />
            </Form.Group>
          </Col>
        </Row>
        {(filtroEstado || filtroNombre || filtroTelefono) && (
          <div className="mt-3">
            <small className="text-muted">
              Mostrando {itemsFiltrados.length} de {items.length} pedidos
            </small>
            <button
              className="btn btn-link btn-sm p-0 ms-2"
              onClick={() => {
                setFiltroEstado('');
                setFiltroNombre('');
                setFiltroTelefono('');
              }}
            >
              Limpiar filtros
            </button>
          </div>
        )}
      </div>

      {loading && <Spinner animation="border" size="sm" />}<br />
      {error && <Alert variant="danger" className="mt-3">{error}</Alert>}
      {!loading && !error && items.length === 0 && (
        <Alert variant="info" className="mt-3">No hay pedidos para mostrar</Alert>
      )}
      {!loading && !error && items.length > 0 && itemsFiltrados.length === 0 && (
        <Alert variant="warning" className="mt-3">
          No se encontraron pedidos que coincidan con los filtros aplicados.
        </Alert>
      )}
      {!loading && !error && itemsFiltrados.length > 0 && (
        <div className="admin-pedidos-table" style={{ position: 'relative' }}>
          <Table hover responsive className="admin-table-modern">
          <thead>
            <tr>
              <th className="text-start" style={{ width: '32px' }}></th>
              <th className="text-start">#</th>
              <th className="text-start">Productos</th>
              <th className="text-start">Fecha</th>
              <th className="text-start">Total</th>
              <th className="text-start">Ganancia</th>
              <th className="text-start">Nombre</th>
              <th className="text-start">Teléfono</th>
              <th className="text-start">Pago</th>
              <th className="text-start text-nowrap">Stock</th>
              <th className="text-start">Estado</th>
            </tr>
          </thead>
          <tbody>
            {itemsFiltrados.map((o, idx) => {
              const pedidoId = o._id;
              if (!pedidoId) {
                console.warn('[AdminPedidos] Pedido sin _id:', o);
                return null;
              }
              const isExpanded = expandedRows.has(pedidoId);
              const isUpdating = updatingStatus.has(pedidoId);
              
              return (
                <React.Fragment key={pedidoId}>
                  <tr 
                    className={`admin-pedido-row ${isExpanded ? 'expanded' : ''}`}
                    onClick={() => toggleRow(pedidoId)}
                  >
                    <td className="text-start">
                      <span className="expand-icon">{isExpanded ? '▼' : '▶'}</span>
                    </td>
                    <td className="text-start text-muted">{itemsFiltrados.length - idx}</td>
                    <td className="text-start">
                      {Array.isArray(o.productos) && o.productos.length > 0
                        ? `${o.productos.length} producto${o.productos.length !== 1 ? 's' : ''}`
                        : '-'}
                    </td>
                    <td className="text-start text-muted">{
                      o.fechaCreacion
                        ? new Date(o.fechaCreacion).toLocaleString('es-AR', { 
                            day: '2-digit', 
                            month: '2-digit', 
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })
                        : (o.createdAt
                            ? (typeof o.createdAt?.toDate === 'function'
                                ? o.createdAt.toDate().toLocaleString('es-AR', { 
                                    day: '2-digit', 
                                    month: '2-digit', 
                                    year: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit'
                                  })
                                : new Date(o.createdAt).toLocaleString('es-AR', { 
                                    day: '2-digit', 
                                    month: '2-digit', 
                                    year: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit'
                                  }))
                            : o.fecha
                              ? new Date(o.fecha).toLocaleString('es-AR', { 
                                  day: '2-digit', 
                                  month: '2-digit', 
                                  year: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })
                              : '-')
                    }</td>
                    <td className="text-start fw-semibold">{
                      (() => {
                        const val = o.montoTotal != null ? o.montoTotal : (o.total != null ? o.total : o.monto);
                        return val != null ? `$${Number(val).toLocaleString()}` : '-';
                      })()
                    }</td>
                    <td className="text-start fw-semibold">{formatGananciaPedido(o)}</td>
                    <td className="text-start">{o.nombreContacto || '-'}</td>
                    <td className="text-start">{o.telefonoContacto || '-'}</td>
                    <td className="text-start">
                      <Badge bg={o.metodoPago === 'efectivo' ? 'info' : 'secondary'}>
                        {getMetodoPagoLabel(o.metodoPago)}
                      </Badge>
                    </td>
                    <td className="text-start text-nowrap">
                      {renderStockBadge(o)}
                    </td>
                    <td className="text-start" onClick={(e) => e.stopPropagation()}>
                      <Dropdown 
                        drop="down" 
                        align="end"
                        popperConfig={{
                          strategy: 'fixed',
                          modifiers: [
                            {
                              name: 'preventOverflow',
                              options: {
                                boundary: 'viewport',
                                padding: 8,
                              },
                            },
                            {
                              name: 'flip',
                              options: {
                                fallbackPlacements: ['top', 'bottom'],
                              },
                            },
                            {
                              name: 'offset',
                              options: {
                                offset: [0, 4],
                              },
                            },
                          ],
                        }}
                      >
                        <Dropdown.Toggle 
                          variant="outline-secondary" 
                          size="sm"
                          disabled={isUpdating}
                          className="admin-status-toggle"
                          id={`dropdown-estado-${pedidoId}`}
                        >
                          {isUpdating ? (
                            <Spinner animation="border" size="sm" />
                          ) : (
                            <Badge bg={getEstadoBadgeVariant(o.estado)} className="admin-status-badge">
                              {o.estado || '-'}
                            </Badge>
                          )}
                        </Dropdown.Toggle>
                        <Dropdown.Menu className="admin-status-menu">
                          {estadosEditables.map(estado => (
                            <Dropdown.Item
                              key={estado}
                              onClick={() => cambiarEstado(pedidoId, estado)}
                              active={o.estado === estado}
                              className={o.estado === estado ? 'active' : ''}
                            >
                              {estado}
                            </Dropdown.Item>
                          ))}
                        </Dropdown.Menu>
                      </Dropdown>
                    </td>
              </tr>
                  {isExpanded && Array.isArray(o.productos) && o.productos.length > 0 && (
                    <tr className="no-hover">
                      <td colSpan="11" className="admin-productos-expanded">
                        <div className="admin-productos-content">
                          <div className="admin-productos-list">
                            {(() => {
                              const tipoCompra = calcularTipoCompra(o);
                              // Usar el orden que ya viene del backend (agrupado por material, nombre desc)
                              const productosOrdenados = o.productos;
                              
                              // Usar contador de materiales del backend
                              // Si viene como Map de Mongoose, convertirlo a objeto
                              let contadorMateriales = {};
                              if (o.contadorMateriales) {
                                if (o.contadorMateriales instanceof Map) {
                                  // Convertir Map a objeto
                                  o.contadorMateriales.forEach((value, key) => {
                                    contadorMateriales[key] = value;
                                  });
                                } else if (typeof o.contadorMateriales === 'object') {
                                  // Ya es un objeto
                                  contadorMateriales = o.contadorMateriales;
                                }
                              }
                              
                              return (
                                <>
                                  {o.stockError && Array.isArray(o.stockErrorDetails) && o.stockErrorDetails.length > 0 && (
                                    <Alert variant="danger" className="mb-3 py-2">
                                      <strong>Error de stock:</strong>
                                      <ul className="mb-0 mt-2">
                                        {o.stockErrorDetails.map((detail, idx) => (
                                          <li key={`${detail.productId || 'prod'}-${idx}`}>
                                            {detail.nombre || detail.productId || 'Producto'}: {detail.error}
                                          </li>
                                        ))}
                                      </ul>
                                    </Alert>
                                  )}

                                  {o.stockError === true && (
                                    <div className="mb-3">
                                      <Button
                                        variant="outline-danger"
                                        size="sm"
                                        disabled={descuentoStockIds.has(o._id)}
                                        onClick={() => descontarStockManual(o._id)}
                                      >
                                        {descuentoStockIds.has(o._id) ? (
                                          <>
                                            <Spinner animation="border" size="sm" className="me-2" />
                                            Descontando stock...
                                          </>
                                        ) : (
                                          'Descontar stock'
                                        )}
                                      </Button>
                                    </div>
                                  )}

                                  <div className="admin-contador-materiales mb-3 p-3" style={{
                                    backgroundColor: '#f8f9fa',
                                    borderRadius: '8px',
                                    border: '1px solid #dee2e6'
                                  }}>
                                    <h6 className="mb-2 fw-bold">Entrega:</h6>
                                    <div className="d-flex flex-wrap gap-2 align-items-center">
                                      <Badge bg={o.modalidadEntrega === 'envio' ? 'primary' : 'secondary'} className="px-3 py-2">
                                        {getModalidadEntregaLabel(o.modalidadEntrega)}
                                      </Badge>
                                    </div>
                                  </div>

                                  {/* Contador por material */}
                                  {Object.keys(contadorMateriales).length > 0 && (
                                    <div className="admin-contador-materiales mb-3 p-3" style={{ 
                                      backgroundColor: '#f8f9fa', 
                                      borderRadius: '8px',
                                      border: '1px solid #dee2e6'
                                    }}>
                                      <h6 className="mb-2 fw-bold">Contador por Material:</h6>
                                      <div className="d-flex flex-wrap gap-2">
                                        {Object.entries(contadorMateriales)
                                          .sort((a, b) => b[1] - a[1]) // Ordenar por cantidad descendente
                                          .map(([material, cantidad]) => (
                                            <Badge key={material} bg="secondary" className="px-3 py-2" style={{ fontSize: '0.9rem' }}>
                                              {material}: {cantidad}
                                            </Badge>
                                          ))}
                                      </div>
                                    </div>
                                  )}
                                  
                                  {/* Lista de productos ordenados */}
                                  {productosOrdenados.map((producto, pIdx) => {
                                    // Determinar precio según tipo de compra
                                    let precioUnitario = 0;
                                    if (tipoCompra === 'mayorista') {
                                      precioUnitario = Number(producto.precio) || 0;
                                    } else {
                                      // Minorista: usar preciomenor
                                      // Primero intentar desde el pedido guardado
                                      if (producto.preciomenor != null && producto.preciomenor !== undefined) {
                                        precioUnitario = Number(producto.preciomenor) || 0;
                                      } else {
                                        // Si no está guardado, usar el cache de Firebase
                                        precioUnitario = productosPrecios[producto.id] || 0;
                                        // Si tampoco está en cache, usar precio como último recurso
                                        if (precioUnitario === 0 && producto.precio != null) {
                                          console.warn(`[AdminPedidos] Producto ${producto.nombre} sin preciomenor disponible, usando precio como fallback`);
                                          precioUnitario = Number(producto.precio) || 0;
                                        }
                                      }
                                    }
                                    const subtotalItem = precioUnitario * (Number(producto.cantidad) || 0);
                                    
                                    return (
                                      <div key={producto._id || producto.id || pIdx} className="admin-producto-item">
                                        <div className="admin-producto-nombre">{producto.nombre || '-'}</div>
                                        <div className="admin-producto-details">
                                          <span className="text-muted">Cantidad: {producto.cantidad || '-'}</span>
                                          <span className="text-muted">•</span>
                                          <span className="text-muted">Precio ({tipoCompra}): ${precioUnitario.toLocaleString()}</span>
                                          {producto.costo != null && (
                                            <>
                                              <span className="text-muted">•</span>
                                              <span className="text-muted">Costo: ${Number(producto.costo).toLocaleString()}</span>
                                            </>
                                          )}
                                          <span className="admin-producto-subtotal">
                                            Subtotal: ${subtotalItem.toLocaleString()}
                                          </span>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </>
                              );
                            })()}
                            <div className="admin-resumen-pedido">
                              {(() => {
                                const tipoCompra = calcularTipoCompra(o);
                                const subtotalProductos = tipoCompra === 'mayorista'
                                  ? o.productos.reduce((acc, p) => acc + ((Number(p.precio) || 0) * (Number(p.cantidad) || 0)), 0)
                                  : o.productos.reduce((acc, p) => {
                                      let preciomenor = Number(p.preciomenor) || 0;
                                      if (preciomenor === 0 && productosPrecios[p.id]) {
                                        preciomenor = productosPrecios[p.id];
                                      }
                                      if (preciomenor === 0 && p.precio) {
                                        preciomenor = Number(p.precio) || 0;
                                      }
                                      return acc + (preciomenor * (Number(p.cantidad) || 0));
                                    }, 0);
                                const costoEnvio = Number(o.costoEnvio) || 0;
                                const totalEnvio = precioEnvioCobrado(o, subtotalProductos);
                                const total = Number(o.montoTotal) || (subtotalProductos + totalEnvio);
                                const costoEnvioInput = Object.prototype.hasOwnProperty.call(costoEnvioEdits, o._id)
                                  ? costoEnvioEdits[o._id]
                                  : String(costoEnvio);
                                const costoTotalProductos = o.costoTotal != null ? Number(o.costoTotal) : null;
                                const gananciaMostrada = costoTotalProductos != null
                                  ? Number(o.montoTotal || total) - costoTotalProductos - costoEnvioActual(o)
                                  : null;
                                
                                return (
                                  <>
                                    <div className="admin-resumen-line">
                                      <span>Total productos:</span>
                                      <span>${subtotalProductos.toLocaleString()}</span>
                                    </div>
                                    <div className="admin-resumen-line">
                                      <span>Total envío:</span>
                                      <span>${totalEnvio.toLocaleString()}</span>
                                    </div>
                                    <div className="admin-resumen-line admin-resumen-total">
                                      <span className="fw-bold">Total del pedido:</span>
                                      <span className="fw-bold">${total.toLocaleString()}</span>
                                    </div>
                                    <div className="admin-resumen-line">
                                      <span>Costo de productos:</span>
                                      <span>{costoTotalProductos != null ? `$${costoTotalProductos.toLocaleString()}` : '—'}</span>
                                    </div>
                                    <div className="admin-resumen-line align-items-center">
                                      <span>Costo del envío:</span>
                                      <span className="d-inline-flex align-items-center gap-2" onClick={(e) => e.stopPropagation()}>
                                        <Form.Control
                                          type="number"
                                          min="0"
                                          step="1"
                                          size="sm"
                                          value={costoEnvioInput}
                                          onChange={(e) => setCostoEnvioEdits(prev => ({ ...prev, [o._id]: e.target.value }))}
                                          style={{ maxWidth: 110 }}
                                          aria-label="Costo del envío"
                                        />
                                        <Button
                                          variant="outline-dark"
                                          size="sm"
                                          disabled={savingCostoEnvioIds.has(o._id)}
                                          onClick={(e) => guardarCostoEnvio(o, e)}
                                        >
                                          {savingCostoEnvioIds.has(o._id) ? 'Guardando...' : 'Guardar'}
                                        </Button>
                                      </span>
                                    </div>
                                    <div className="admin-resumen-line admin-resumen-total">
                                      <span className="fw-bold">Ganancia:</span>
                                      <span className="fw-bold">{gananciaMostrada != null ? `$${gananciaMostrada.toLocaleString()}` : '—'}</span>
                                    </div>
                                  </>
                                );
                              })()}
                            </div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </Table>
        </div>
      )}
    </Container>
  );
}

export default AdminPedidos;


